import { fetchWithChatFeedback } from './chatFeedbackRequired';
/**
 * Public Self-Signup API Functions
 *
 * These endpoints are PUBLIC (the login screen is pre-auth), so the calls use
 * direct `fetch` with `credentials: 'include'` and NO auth headers, mirroring
 * EmailGate's public `/api/auth/send-code` style. They must never depend on a
 * session cookie or the access-request secret.
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

export type SelfSignupStatus = 'pending' | 'duplicate_pending' | 'already_whitelisted' | 'approved';

export interface SelfSignupPayload {
  email: string;
  first_name: string;
  last_name: string;
  unit: string;
  category: string;
  heard_about: string;
}

export interface SelfSignupResult {
  id?: string;
  status: SelfSignupStatus;
}

type SelfSignupApiEnvelope = {
  success?: unknown;
  error?: unknown;
  data?: {
    id?: unknown;
    status?: unknown;
  };
};

const SELF_SIGNUP_STATUSES: readonly SelfSignupStatus[] = [
  'pending',
  'duplicate_pending',
  'already_whitelisted',
  'approved',
];

function isSelfSignupStatus(value: unknown): value is SelfSignupStatus {
  return (
    typeof value === 'string' &&
    SELF_SIGNUP_STATUSES.includes(value as SelfSignupStatus)
  );
}

/**
 * Fetch the canonical unit list for the signup dropdown.
 * `GET /api/signup/units` (public, no auth) → `{ units: string[] }`.
 */
export async function fetchSignupUnits(): Promise<string[]> {
  const res = await fetchWithChatFeedback(`${API_BASE_URL}/api/signup/units`, {
    method: 'GET',
    credentials: 'include',
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch units (HTTP ${res.status})`);
  }
  const data = await res.json();
  // Backend shape: { units: [...] }. Tolerate an ApiResponse-style
  // { data: { units: [...] } } wrapper as well.
  return data?.units ?? data?.data?.units ?? [];
}

/**
 * Submit a self-signup request.
 * `POST /api/signup` (public, no auth, no secret) → ApiResponse
 * `{ data: { id?, status } }`.
 */
export async function submitSelfSignup(
  payload: SelfSignupPayload,
): Promise<SelfSignupResult> {
  const res = await fetchWithChatFeedback(`${API_BASE_URL}/api/signup`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error(`Signup failed (HTTP ${res.status})`);
  }
  const body = (await res.json()) as SelfSignupApiEnvelope;
  if (body?.success !== true) {
    throw new Error(
      typeof body?.error === 'string' && body.error
        ? body.error
        : 'Signup request could not be submitted. Please try again.',
    );
  }
  const data = body.data;
  if (!data || !isSelfSignupStatus(data.status)) {
    throw new Error('Signup response was invalid. Please try again.');
  }
  return {
    id: typeof data.id === 'string' ? data.id : undefined,
    status: data.status,
  };
}
